import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  styled,
  alpha,
  useTheme,
  Box,
  InputBase,
  IconButton,
  Paper,
  Chip,
  Stack,
  Typography,
  Tooltip,
  Fade,
  Zoom,
  Collapse,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Mic as MicIcon,
  FilterList as FilterIcon,
  History as HistoryIcon,
  TrendingUp as TrendingIcon,
  AutoAwesome as AutoIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

// Enhanced search suggestions and recent searches
const SUGGESTED_SEARCHES = [
  "Chicken curry",
  "Pasta recipes",
  "Healthy salads",
  "Quick breakfast",
  "Vegetarian meals",
  "Dessert recipes",
  "Low carb dishes",
  "Spicy food",
];

const TRENDING_SEARCHES = [
  "Air fryer recipes",
  "One-pot meals",
  "Keto breakfast",
  "Mediterranean diet",
];

const SearchRecipe = ({
  setSearchTerm,
  isSearching = false,
  searchTerm = "",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [value, setValue] = useState(searchTerm);
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);

  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const recognitionRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== "undefined") {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        setVoiceSupported(true);
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = false;
        recognitionRef.current.lang = "en-US";

        recognitionRef.current.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          setValue(transcript);
          handleChange({ target: { value: transcript } });
          setIsListening(false);
        };

        recognitionRef.current.onerror = (event) => {
          console.error("Speech recognition error:", event.error);
          setIsListening(false);
        };

        recognitionRef.current.onend = () => {
          setIsListening(false);
        };
      }
    }
  }, []);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem("recentSearches");
      if (stored) {
        setRecentSearches(JSON.parse(stored).slice(0, 5));
      }
    } catch (error) {
      console.error("Failed to load recent searches:", error);
    }
  }, []);

  // Save to recent searches
  const saveToRecentSearches = useCallback(
    (term) => {
      if (!term.trim() || term.length < 2) return;

      try {
        const updated = [
          term,
          ...recentSearches.filter((s) => s !== term),
        ].slice(0, 5);
        setRecentSearches(updated);
        localStorage.setItem("recentSearches", JSON.stringify(updated));
      } catch (error) {
        console.error("Failed to save recent search:", error);
      }
    },
    [recentSearches]
  );

  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setValue(newValue);

      // Clear any existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set a new timeout of 800ms for better UX
      searchTimeoutRef.current = setTimeout(() => {
        setSearchTerm(newValue);
        if (newValue.trim()) {
          saveToRecentSearches(newValue.trim());
        }
      }, 800);
    },
    [setSearchTerm, saveToRecentSearches]
  );

  const handleClear = useCallback(() => {
    setValue("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchTerm("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  }, [setSearchTerm]);

  const handleSuggestionClick = useCallback(
    (suggestion) => {
      setValue(suggestion);
      setSearchTerm(suggestion);
      saveToRecentSearches(suggestion);
      setShowSuggestions(false);
      inputRef.current?.blur();
    },
    [setSearchTerm, saveToRecentSearches]
  );

  const handleVoiceSearch = useCallback(() => {
    if (recognitionRef.current && !isListening && voiceSupported) {
      try {
        setIsListening(true);
        recognitionRef.current.start();
      } catch (error) {
        console.error("Voice search error:", error);
        setIsListening(false);
      }
    }
  }, [isListening, voiceSupported]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setShowSuggestions(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    // Delay hiding suggestions to allow clicking
    setTimeout(() => setShowSuggestions(false), 200);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        if (value.trim()) {
          saveToRecentSearches(value.trim());
        }
        setShowSuggestions(false);
        inputRef.current?.blur();
      }
      if (e.key === "Escape") {
        setShowSuggestions(false);
        inputRef.current?.blur();
      }
    },
    [value, saveToRecentSearches]
  );

  // Clean up timeout when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Filtered suggestions based on current input
  const filteredSuggestions = value.trim()
    ? SUGGESTED_SEARCHES.filter(
        (s) =>
          s.toLowerCase().includes(value.toLowerCase()) &&
          s.toLowerCase() !== value.toLowerCase()
      )
    : [];

  return (
    <SearchContainer>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
      >
        <SearchWrapper
          theme={theme}
          $isFocused={isFocused}
          $isSearching={isSearching}
        >
          {/* Main search input */}
          <StyledInputBase
            ref={inputRef}
            value={value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder="Search for delicious recipes..."
            theme={theme}
            $isFocused={isFocused}
            startAdornment={
              <SearchIconWrapper theme={theme} $isSearching={isSearching}>
                <motion.div
                  animate={isSearching ? { rotate: 360 } : { rotate: 0 }}
                  transition={{
                    duration: 1,
                    repeat: isSearching ? Infinity : 0,
                    ease: "linear",
                  }}
                >
                  <SearchIcon />
                </motion.div>
              </SearchIconWrapper>
            }
            endAdornment={
              <SearchActions>
                <AnimatePresence>
                  {/* Voice search button */}
                  {voiceSupported && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0 }}
                    >
                      <Tooltip title="Voice Search" arrow>
                        <IconButton
                          onClick={handleVoiceSearch}
                          size="small"
                          disabled={isListening}
                          sx={{
                            color: isListening
                              ? theme.palette.error.main
                              : theme.palette.text.secondary,
                            backgroundColor: isListening
                              ? alpha(theme.palette.error.main, 0.1)
                              : "transparent",
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.1
                              ),
                              color: theme.palette.primary.main,
                            },
                          }}
                        >
                          <motion.div
                            animate={
                              isListening
                                ? { scale: [1, 1.2, 1] }
                                : { scale: 1 }
                            }
                            transition={{
                              duration: 0.5,
                              repeat: isListening ? Infinity : 0,
                            }}
                          >
                            <MicIcon fontSize="small" />
                          </motion.div>
                        </IconButton>
                      </Tooltip>
                    </motion.div>
                  )}

                  {/* Filter toggle */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <Tooltip title="Filters" arrow>
                      <IconButton
                        onClick={() => setShowFilters(!showFilters)}
                        size="small"
                        sx={{
                          color: showFilters
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                          backgroundColor: showFilters
                            ? alpha(theme.palette.primary.main, 0.1)
                            : "transparent",
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.1
                            ),
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        <FilterIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </motion.div>

                  {/* Clear button */}
                  {value && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <Tooltip title="Clear" arrow>
                        <IconButton
                          onClick={handleClear}
                          size="small"
                          sx={{
                            color: theme.palette.text.secondary,
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.error.main,
                                0.1
                              ),
                              color: theme.palette.error.main,
                            },
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </motion.div>
                  )}
                </AnimatePresence>
              </SearchActions>
            }
          />

          {/* Floating label with better animation */}
          <FloatingLabel
            theme={theme}
            $focused={isFocused || value.length > 0}
            $isSearching={isSearching}
          >
            <motion.span
              animate={
                isFocused || value.length > 0
                  ? { scale: 0.8, y: -8 }
                  : { scale: 1, y: 0 }
              }
              transition={{ duration: 0.2 }}
            >
              {isSearching
                ? "Searching recipes..."
                : "Find your perfect recipe"}
            </motion.span>
          </FloatingLabel>

          {/* Search progress indicator */}
          <AnimatePresence>
            {isSearching && (
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                exit={{ scaleX: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "2px",
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  transformOrigin: "left",
                }}
              />
            )}
          </AnimatePresence>
        </SearchWrapper>

        {/* Enhanced suggestions dropdown */}
        <AnimatePresence>
          {showSuggestions && (isFocused || value) && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <SuggestionsContainer theme={theme}>
                {/* Current search indicator */}
                {value.trim() && (
                  <SearchIndicator>
                    <AutoIcon fontSize="small" />
                    <Typography variant="body2">
                      Search for: <strong>"{value}"</strong>
                    </Typography>
                  </SearchIndicator>
                )}

                {/* Filtered suggestions */}
                {filteredSuggestions.length > 0 && (
                  <SuggestionSection>
                    <SectionHeader>
                      <SearchIcon fontSize="small" />
                      <Typography variant="caption" fontWeight={600}>
                        Suggestions
                      </Typography>
                    </SectionHeader>
                    <SuggestionsList>
                      {filteredSuggestions.map((suggestion, index) => (
                        <motion.div
                          key={suggestion}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <SuggestionChip
                            label={suggestion}
                            onClick={() => handleSuggestionClick(suggestion)}
                            variant="outlined"
                            size="small"
                          />
                        </motion.div>
                      ))}
                    </SuggestionsList>
                  </SuggestionSection>
                )}

                {/* Recent searches */}
                {recentSearches.length > 0 && !value.trim() && (
                  <SuggestionSection>
                    <SectionHeader>
                      <HistoryIcon fontSize="small" />
                      <Typography variant="caption" fontWeight={600}>
                        Recent Searches
                      </Typography>
                    </SectionHeader>
                    <SuggestionsList>
                      {recentSearches.map((search, index) => (
                        <motion.div
                          key={search}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <SuggestionChip
                            label={search}
                            onClick={() => handleSuggestionClick(search)}
                            variant="filled"
                            size="small"
                            color="secondary"
                          />
                        </motion.div>
                      ))}
                    </SuggestionsList>
                  </SuggestionSection>
                )}

                {/* Trending searches */}
                {!value.trim() && (
                  <SuggestionSection>
                    <SectionHeader>
                      <TrendingIcon fontSize="small" />
                      <Typography variant="caption" fontWeight={600}>
                        Trending
                      </Typography>
                    </SectionHeader>
                    <SuggestionsList>
                      {TRENDING_SEARCHES.map((trend, index) => (
                        <motion.div
                          key={trend}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <SuggestionChip
                            label={trend}
                            onClick={() => handleSuggestionClick(trend)}
                            variant="outlined"
                            size="small"
                            color="primary"
                          />
                        </motion.div>
                      ))}
                    </SuggestionsList>
                  </SuggestionSection>
                )}
              </SuggestionsContainer>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick filters */}
        <Collapse in={showFilters}>
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FiltersContainer theme={theme}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Quick Filters
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {[
                  "Vegetarian",
                  "Quick & Easy",
                  "Healthy",
                  "Desserts",
                  "Main Course",
                ].map((filter) => (
                  <Chip
                    key={filter}
                    label={filter}
                    size="small"
                    variant="outlined"
                    onClick={() => handleSuggestionClick(filter)}
                    sx={{
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  />
                ))}
              </Stack>
            </FiltersContainer>
          </motion.div>
        </Collapse>
      </motion.div>
    </SearchContainer>
  );
};

// Enhanced styled components with better animations
const SearchContainer = styled(Box)({
  position: "relative",
  width: "100%",
  maxWidth: "700px",
  margin: "2rem auto",
});

const SearchWrapper = styled(Paper)(({ theme, $isFocused, $isSearching }) => ({
  position: "relative",
  borderRadius: "24px",
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.95
        )} 0%, ${alpha(theme.palette.grey[900], 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.98
        )} 0%, ${alpha(theme.palette.grey[50], 0.95)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `2px solid ${
    $isFocused ? theme.palette.primary.main : alpha(theme.palette.divider, 0.2)
  }`,
  boxShadow: $isFocused
    ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.2)}, 0 0 0 4px ${alpha(
        theme.palette.primary.main,
        0.1
      )}`
    : `0 8px 32px ${alpha(
        theme.palette.common.black,
        theme.palette.mode === "dark" ? 0.4 : 0.1
      )}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "hidden",

  "&:hover": {
    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
    transform: "translateY(-2px)",
    border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  },

  ...($isSearching && {
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background: `linear-gradient(90deg, transparent, ${alpha(
        theme.palette.primary.main,
        0.1
      )}, transparent)`,
      animation: "shimmer 2s infinite",
    },
  }),

  "@keyframes shimmer": {
    "0%": { left: "-100%" },
    "100%": { left: "100%" },
  },
}));

const StyledInputBase = styled(InputBase)(({ theme, $isFocused }) => ({
  color: theme.palette.text.primary,
  width: "100%",
  padding: "1.5rem 2rem",
  paddingLeft: "4rem",
  paddingRight: "6rem",
  fontSize: "1.1rem",
  fontWeight: 500,

  "& .MuiInputBase-input": {
    transition: "all 0.3s ease",
    "&::placeholder": {
      opacity: $isFocused ? 0.8 : 0.6,
      color: theme.palette.text.secondary,
      transition: "opacity 0.3s ease",
    },
    "&:focus": {
      "&::placeholder": {
        opacity: 0.4,
      },
    },
  },

  [theme.breakpoints.down("sm")]: {
    padding: "1.2rem 1.5rem",
    paddingLeft: "3.5rem",
    paddingRight: "5rem",
    fontSize: "1rem",
  },
}));

const SearchIconWrapper = styled("div")(({ theme, $isSearching }) => ({
  position: "absolute",
  left: "1.5rem",
  top: "50%",
  transform: "translateY(-50%)",
  color: $isSearching
    ? theme.palette.primary.main
    : theme.palette.text.secondary,
  zIndex: 1,
  transition: "color 0.3s ease",

  [theme.breakpoints.down("sm")]: {
    left: "1rem",
  },
}));

const SearchActions = styled(Box)({
  position: "absolute",
  right: "1rem",
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  zIndex: 1,
});

const FloatingLabel = styled("span")(({ theme, $focused, $isSearching }) => ({
  position: "absolute",
  left: "4rem",
  top: "50%",
  transform: $focused ? "translateY(-170%) scale(0.8)" : "translateY(-50%)",
  transformOrigin: "left center",
  color: $isSearching
    ? theme.palette.primary.main
    : $focused
    ? theme.palette.primary.main
    : theme.palette.text.secondary,
  pointerEvents: "none",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  padding: "0 0.5rem",
  borderRadius: "4px",
  fontSize: "0.9rem",
  fontWeight: 500,
  backdropFilter: "blur(4px)",

  [theme.breakpoints.down("sm")]: {
    left: "3.5rem",
    fontSize: "0.8rem",
  },
}));

const SuggestionsContainer = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  marginTop: "0.5rem",
  borderRadius: "16px",
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.98
        )} 0%, ${alpha(theme.palette.grey[900], 0.95)} 100%)`
      : `linear-gradient(135deg, ${alpha(
          theme.palette.background.paper,
          0.98
        )} 0%, ${alpha(theme.palette.grey[50], 0.95)} 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  boxShadow: `0 20px 60px ${alpha(
    theme.palette.common.black,
    theme.palette.mode === "dark" ? 0.5 : 0.15
  )}`,
  padding: "1rem",
  maxHeight: "400px",
  overflowY: "auto",
  zIndex: 1000,
}));

const SuggestionSection = styled(Box)(({ theme }) => ({
  marginBottom: "1rem",
  "&:last-child": {
    marginBottom: 0,
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  marginBottom: "0.5rem",
  color: theme.palette.text.secondary,
}));

const SuggestionsList = styled(Stack)({
  direction: "row",
  spacing: 1,
  flexWrap: "wrap",
  useFlexGap: true,
});

const SuggestionChip = styled(Chip)(({ theme }) => ({
  transition: "all 0.2s ease",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
  },
}));

const SearchIndicator = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.75rem",
  marginBottom: "1rem",
  borderRadius: "8px",
  background: alpha(theme.palette.primary.main, 0.1),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const FiltersContainer = styled(Box)(({ theme }) => ({
  marginTop: "1rem",
  padding: "1rem",
  borderRadius: "16px",
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

SearchRecipe.propTypes = {
  setSearchTerm: PropTypes.func.isRequired,
  isSearching: PropTypes.bool,
  searchTerm: PropTypes.string,
};

export default React.memo(SearchRecipe);
